import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { graphql, createFragmentContainer } from 'react-relay';

// components
import { PdpRespSimilarItemCarousel } from './PdpRespSimilarItemCarousel/PdpRespSimilarItemCarousel';
import { PdpRespSimilarItemsContainer } from './PdpRespSimilarItemsContainer/PdpRespSimilarItemsContainer';
import { UnpurchasableHeader, viewSimilarItems, ItemsSimilarToHeader } from '../pdpMessages';
import { PdpSharedSoldHeader } from '../PdpSharedSoldIcons/PdpSharedSoldHeader';
import { Spinner } from 'dibs-elements/exports/Spinner';

// actions
import { hasFiredLandingItemsImpressions } from '../../actions/pdpRespSimilarItemsActions';

// helpers
import get from 'lodash.get';
import classnames from 'classnames';
import scrollIntoView from 'scroll-into-view';
import {
    trackItemClick,
    trackEcommerceLandingItemClick,
    trackLandingViewAllClick,
    trackLandingItemImpressions,
    trackItemsDisplayed,
} from '../tracking/similarItemsTracking';

// styles
import styles from './PdpRespSimilarItemsLanding.scss';

// constants
const PURCHASABLE_ITEM_COUNT = 6;

export class PdpRespSimilarItemsLandingComponent extends React.Component {
    constructor(props) {
        super(props);

        this.hasFiredPdpTileTestTracking = false;
        this.onClickItem = this.onClickItem.bind(this);
        this.onClickViewMore = this.onClickViewMore.bind(this);
    }

    componentDidMount() {
        trackItemsDisplayed({ moduleType: 'PDP_SIMILAR_ITEMS' });
    }

    componentDidUpdate() {
        const landingItems = this.getLandingItems();
        if (
            landingItems.length &&
            !this.props.similarItemsState.hasFiredImpressions &&
            get(this.props, 'similarItems.edges[0].node.ecommerceTrackingParams')
        ) {
            trackLandingItemImpressions(landingItems, this.isPdpTypeDisplayUnpurchasable());
            this.props.updateHasFiredImpressions();
        }
    }

    scrollToPdpRespSimilarItemsGrid() {
        const gridEl = this.props.scrollToRef?.current;
        if (gridEl) {
            scrollIntoView(
                gridEl,
                {
                    time: 500,
                },
                () => gridEl.focus()
            );
        }
    }

    onClickItem(index) {
        trackItemClick({ moduleType: 'PDP_SIMILAR_ITEMS' });
        trackEcommerceLandingItemClick({
            index,
            landingItem: this.getLandingItems()[index],
            isSoldPdp: this.isPdpTypeDisplayUnpurchasable(),
        });
    }

    onClickViewMore() {
        this.scrollToPdpRespSimilarItemsGrid();

        // fire tracking
        trackLandingViewAllClick();
    }

    isPdpTypeDisplayUnpurchasable() {
        const { item } = this.props;
        const isListingNotListed = item.isListingNotListed && !get(item, 'seller.isInternal');
        return (item.isListingUnlisted || item.isSold) && !isListingNotListed;
    }

    getLandingItems() {
        const similarItems = get(this.props, 'similarItems.edges') || [];

        // Unpurchasable Pdp
        if (this.isPdpTypeDisplayUnpurchasable()) {
            return similarItems;
        }

        // GROWTH-1429: Test Similar Items on Pdp
        if (this.props.relayVariables.showSimilarItems) {
            return similarItems.slice(0, PURCHASABLE_ITEM_COUNT);
        }

        return [];
    }

    render() {
        const { showSimilarItems } = this.props.relayVariables;
        const itemTitle = <i>{this.props.item.title}</i>;
        let landingItems = this.getLandingItems();
        let additionalLandingItems;

        let header;
        let subtitle;
        let isHorizontal = false;

        if (!showSimilarItems && !this.isPdpTypeDisplayUnpurchasable()) {
            return null;
        }

        if (this.isPdpTypeDisplayUnpurchasable()) {
            // Unpurchasable Pdp
            const { item } = this.props;
            if (item.isSold) {
                header = <PdpSharedSoldHeader item={item} />;
            } else {
                header = <UnpurchasableHeader title={itemTitle} />;
            }
            subtitle = viewSimilarItems;
        } else if (showSimilarItems) {
            // GROWTH-1429: Test Similar Items on Pdp
            subtitle = <ItemsSimilarToHeader title={itemTitle} />;
            isHorizontal = true;
        }

        if (landingItems.length > PURCHASABLE_ITEM_COUNT) {
            additionalLandingItems = landingItems.slice(PURCHASABLE_ITEM_COUNT);
            landingItems = landingItems.slice(0, PURCHASABLE_ITEM_COUNT);
        }

        const itemProps = {
            onClick: this.onClickItem,
            isCondensed: isHorizontal,
        };

        const landingItemsClassNames = classnames(styles.landingItems, 'rowFlex', {
            [styles.isHorizontal]: isHorizontal,
        });
        const landingItemClassNames = `colLg2 colMd4 colXs6 ${styles.landingItem}`;
        const spinnerClasses = classnames(styles.spinner, {
            [styles.isUnpurchasable]: this.isPdpTypeDisplayUnpurchasable(),
        });

        return (
            <PdpRespSimilarItemsContainer
                isDark
                isTopContainer
                header={header}
                subtitle={subtitle}
                onClickViewMore={this.onClickViewMore}
                dataTn="pdp-similar-landing-grid"
                isHorizontal={isHorizontal}
                item={this.props.item}
            >
                {/* Grid of Items */}
                {!landingItems.length ? (
                    <Spinner containerClass={spinnerClasses} />
                ) : (
                    <React.Fragment>
                        <div className={landingItemsClassNames}>
                            {landingItems.map(({ node }, index) => (
                                <div key={node.serviceId} className={landingItemClassNames}>
                                    <PdpRespSimilarItemCarousel
                                        item={node}
                                        index={index}
                                        {...itemProps}
                                    />
                                </div>
                            ))}
                        </div>
                        {additionalLandingItems ? (
                            <div className={landingItemsClassNames}>
                                {additionalLandingItems.map(({ node }, index) => (
                                    <div key={node.serviceId} className={landingItemClassNames}>
                                        <PdpRespSimilarItemCarousel
                                            item={node}
                                            index={index + 6}
                                            {...itemProps}
                                        />
                                    </div>
                                ))}
                            </div>
                        ) : null}
                    </React.Fragment>
                )}
            </PdpRespSimilarItemsContainer>
        );
    }
}

PdpRespSimilarItemsLandingComponent.propTypes = {
    relayVariables: PropTypes.shape({
        showSimilarItems: PropTypes.bool.isRequired,
        isClient: PropTypes.bool.isRequired,
    }).isRequired,
    item: PropTypes.object.isRequired,
    similarItems: PropTypes.object,
    user: PropTypes.object.isRequired,
    scrollToRef: PropTypes.object.isRequired,
    // redux
    similarItemsState: PropTypes.shape({
        hasFiredImpressions: PropTypes.bool.isRequired,
    }).isRequired,
    updateHasFiredImpressions: PropTypes.func.isRequired,
};

// Redux Map Functions
function mapStateToProps({ similarItems }) {
    return {
        similarItemsState: {
            hasFiredImpressions: similarItems.ecommerceImpressions.hasFiredLandingItems,
        },
    };
}
function mapDispatchToProps(dispatch) {
    return {
        updateHasFiredImpressions: () => dispatch(hasFiredLandingItemsImpressions()),
    };
}

export const PdpRespSimilarItemsLanding = createFragmentContainer(
    connect(mapStateToProps, mapDispatchToProps)(PdpRespSimilarItemsLandingComponent),
    {
        item: graphql`
            fragment PdpRespSimilarItemsLanding_item on Item {
                title
                isSold
                isListingNotListed
                isListingUnlisted
                seller {
                    isInternal
                }
                ...PdpSharedSoldHeader_item
                ...PdpRespSimilarItemsContainer_item
            }
        `,
        similarItems: graphql`
            fragment PdpRespSimilarItemsLanding_similarItems on moreLikeThisItemConnection {
                edges {
                    node {
                        serviceId
                        categoryPath
                        categoryCode
                        browseUrl
                        title
                        ecommerceTrackingParams
                        ...PdpRespSimilarItemCarousel_item
                    }
                }
            }
        `,
    }
);
